import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Newspaper, ArrowRight, User, Loader2 } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";
import Pagination from "@/components/Pagination";
import ImageFallback from "@/components/ImageFallback";
import { usePagination } from "@/hooks/usePagination";

const categories = ["Semua", "Akademik", "Prestasi", "Pengumuman", "Ekstrakurikuler"];

const Berita = () => {
  const [news, setNews] = useState([]);
  const [cat, setCat] = useState("Semua");
  const [loading, setLoading] = useState(true);
  const { page, totalPages, pageItems, setPage, resetPage, total, pageSize } = usePagination(news);
  useSeo("Berita", "Berita dan informasi terkini seputar Madrasah Ibtidaiyah Futuhiyyah Doro, Pekalongan.");

  useEffect(() => {
    setLoading(true);
    api.get(`/news${cat !== "Semua" ? `?category=${encodeURIComponent(cat)}` : ""}`).then((r) => setNews(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [cat]);

  return (
    <div>
      <PageHeader title="Berita Madrasah" subtitle="Informasi, pengumuman, dan kabar terbaru dari MI Futuhiyyah Doro." crumbs={[{ label: "Berita" }]} />
      <div className="mx-auto max-w-6xl px-6 py-14">
        <div className="mb-10 flex flex-wrap justify-center gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => { if (c !== cat) { setCat(c); resetPage(); } }}
              data-testid={`news-category-${c.toLowerCase()}`}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-all ${cat === c ? "bg-emerald-600 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:border-emerald-200 hover:text-emerald-700"}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
        ) : news.length === 0 ? (
          <div className="py-20 text-center text-slate-400" data-testid="news-empty-state">
            <Newspaper className="mx-auto h-12 w-12 opacity-40" />
            <p className="mt-3">Belum ada berita untuk kategori ini.</p>
          </div>
        ) : (
          <>
          <p className="mb-6 text-center text-sm text-slate-400" data-testid="news-count-info">
            Menampilkan {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} dari {total} berita
          </p>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
            {pageItems.map((n, i) => (
              <motion.div key={`${page}-${n.id}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }}>
                <Link to={`/berita/${n.slug}`} data-testid={`news-card-${n.id}`} className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    {n.image_url ? (
                      <img src={resolveImg(n.image_url)} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <ImageFallback />
                    )}
                    <span className="absolute left-3 top-3 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">{n.category}</span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Newspaper className="h-3.5 w-3.5" />{formatDate(n.date)}</span>
                      <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{n.author}</span>
                    </div>
                    <h3 className="mt-2 font-heading text-base font-semibold text-slate-800 line-clamp-2 group-hover:text-emerald-700">{n.title}</h3>
                    <p className="mt-2 flex-1 text-sm text-slate-500 line-clamp-3">{n.excerpt}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700">Baca Selengkapnya <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} prefix="news-pagination" />
          </>
        )}
      </div>
    </div>
  );
};

export default Berita;
